import React, { useState } from "react";
import isEqual from "react-fast-compare";
import { HotelRoomCard } from "./HotelRoomCard";
import { HotelRoomParam } from "main/javascripts/features/hotel/components/hotelRoom/HotelRoomParam";
import { css } from "@emotion/react";
import { SelectedHotelPlanDetail } from "./SelectedHotelPlanDetail";
import { HotelRoomSearchForm } from "./HotelRoomSearchForm";
import { Icon } from "main/javascripts/components/icon/Icon";
import { SocialWebViewSelectionModalDialog } from "main/javascripts/components/molecules/Modal/SocialWebViewSelectionModalDialog";
import { HotelRoomCardSkeleton } from "main/javascripts/features/hotel/components/hotelRoom/HotelRoomCardSkeleton";
import { HotelRoomNotAvailableMessage } from "main/javascripts/features/hotel/components/HotelRoomNotAvailableMessage";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import {
  hotelSelector,
  isLoadingRoomSelector,
  requestRoomErrorSelector,
  selectedHotelPlanSelector,
  selectedHotelRoomSelector,
  setDisplayHotelRoomDetailModal,
  setDisplayHotelRoomImageModal,
  setHotelRoomModalDetails,
} from "../../hotelSlice";
import { hotelShowParamSelector } from "main/javascripts/slices/paramSlice";
import { setHotelRoomModalImages } from "main/javascripts/actions/Hotels";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  backgroundColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import {
  isFacebookMessengerInitializedInSelector,
  isLIFFInitializedInSelector,
  loggedInSocialMediaSelector,
} from "main/javascripts/slices/currentUserSlice";

const HotelRoomListWithSelectedPlanComponent: React.FC =
  (): React.ReactElement => {
    const appDispatch = useAppDispatch();

    const hotel = useAppSelector(hotelSelector);
    const selectedHotelRoom = useAppSelector(selectedHotelRoomSelector);
    const selectedHotelPlan = useAppSelector(selectedHotelPlanSelector);
    const param = useAppSelector(hotelShowParamSelector);
    const isLoadingRoom = useAppSelector(isLoadingRoomSelector);
    const requestRoomError = useAppSelector(requestRoomErrorSelector);

    const initializedLIFF = useAppSelector(isLIFFInitializedInSelector);
    const initializedFacebookMessenger = useAppSelector(
      isFacebookMessengerInitializedInSelector
    );
    const loggedInSocialMedia = useAppSelector(loggedInSocialMediaSelector);

    const [isDisplayedForm, setIsDisplayedForm] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [hasCostMessage, setHasCostMessage] = useState(false);

    const displayForm: () => void = () => {
      setIsDisplayedForm(true);
    };

    const hideForm: () => void = () => {
      setIsDisplayedForm(false);
    };

    const onClickRoomImage: (images) => void = (images) => {
      appDispatch(setHotelRoomModalImages(images));
      appDispatch(setDisplayHotelRoomImageModal(true));
    };

    const onClickRoomDetail: (data: any) => void = (data: any) => {
      appDispatch(setHotelRoomModalDetails(data));
      appDispatch(setDisplayHotelRoomDetailModal(true));
    };

    const onSelectHotelPlan: (hasCostMessage: boolean) => void = (
      hasCostMessage: boolean
    ) => {
      setIsSelected(true);
      setHasCostMessage(hasCostMessage);
    };

    const selectionModalCloseHandler: () => void = () => {
      setIsSelected(false);
    };

    const renderSelectedHotelPlan: () => JSX.Element = () => {
      return selectedHotelRoom && selectedHotelPlan ? (
        <SelectedHotelPlanDetail
          param={param}
          selectedHotelId={hotel.id}
          selectedHotelRoom={selectedHotelRoom}
          selectedHotelPlan={selectedHotelPlan}
          onClickImage={onClickRoomImage}
          onClickDetail={onClickRoomDetail}
        />
      ) : (
        <div css={stockNoticeTextStyle}>
          チャットでご案内したプランは売り切れになりました
        </div>
      );
    };

    const renderList: () => JSX.Element = () => {
      const roomCount = 3;
      if (requestRoomError) {
        return <HotelRoomNotAvailableMessage />;
      }
      return (
        <>
          {hotel.hotelRooms && (
            <div>
              {renderSelectedHotelPlan()}
              <div css={priceNoticeBlockStyle}>
                <span css={priceNoticeIconStyle}>
                  <Icon styleKey="caution" />
                </span>
                <span css={priceNoticeTextStyle}>
                  料金は常に変動しています。チャットの【合計料金】との差額が表示されています。
                </span>
              </div>
            </div>
          )}
          {!isLoadingRoom
            ? hotel.hotelRooms.map((hotelRoom) => {
                return (
                  <HotelRoomCard
                    key={hotelRoom.id}
                    hotelId={hotel.id}
                    hotelRoom={hotelRoom}
                    param={param}
                    onClickImage={onClickRoomImage}
                    onClickDetail={onClickRoomDetail}
                    onSelectHotelPlan={onSelectHotelPlan}
                    loggedInSocialMedia={loggedInSocialMedia}
                  />
                );
              })
            : Array.from(Array(roomCount)).map((_, index) => {
                return <HotelRoomCardSkeleton key={index} />;
              })}
        </>
      );
    };

    if (!hotel.isEan) {
      return null;
    }
    const isNoParam: any = (): boolean => {
      if (!param.checkinDate && !param.checkoutDate) {
        return true;
      }
      return false;
    };

    // TODO: SocialWebViewSelectionModalDialogは旧コンポーネントを使用している
    return (
      <div css={roomListBlockStyle}>
        <SocialWebViewSelectionModalDialog
          displayed={isSelected}
          closeHandler={selectionModalCloseHandler}
          selectionType="hotel"
          initializedFacebookMessenger={initializedFacebookMessenger}
          initializedLIFF={initializedLIFF}
          hasCostMessage={hasCostMessage}
        />
        <div css={roomListInnerStyle}>
          <h2 css={headlineStyle}>客室を選択</h2>
          {isDisplayedForm || isNoParam() ? (
            <>
              <HotelRoomSearchForm />
              {!isNoParam() ? (
                <div css={linkBlockStyle}>
                  <a css={linkSwitchStyle} onClick={hideForm}>
                    <span css={linkIconStyle}>
                      <Icon styleKey="close" />
                    </span>
                    変更をキャンセル
                  </a>
                </div>
              ) : (
                ""
              )}
            </>
          ) : (
            <>
              <HotelRoomParam param={param} displayForm={displayForm} />
            </>
          )}
          {renderList()}
        </div>
      </div>
    );
  };

const areEqual: any = (prevProps: any, nextProps: any): any => {
  return isEqual(prevProps, nextProps);
};
export const HotelRoomListWithSelectedPlan: any = React.memo(
  HotelRoomListWithSelectedPlanComponent,
  areEqual
);

const arrowSize = "0.8rem";

const headlineStyle = css`
  line-height: ${lineHeight.headline2};
  padding: calc(
      ${space.atom} - (${lineHeight.headline2} - ${fontSize.headline}) / 2
    )
    ${space.atom} 0;
  margin: 0;
  font-size: ${fontSize.mediumHeading};
  color: ${textColor.primaryDarkColor};
  font-weight: ${fontWeight.bold};
  text-align: center;
  letter-spacing: 0.2em;
`;
const linkBlockStyle = css`
  text-align: center;
  margin-top: -${space.atom2x};
`;
const roomListBlockStyle = css`
  max-width: 100%;
  box-sizing: border-box;
  background-color: ${backgroundColor.secondaryLightColor};
  position: relative;
  margin-top: ${space.atom2x};
  padding: ${space.atom} 0 0;
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    display: block;
    width: 0;
    height: 0;
    border-left: ${arrowSize} solid transparent;
    border-right: ${arrowSize} solid transparent;
    border-top: ${arrowSize} solid white;
    transform: translateX(-50%);
  }
`;
const roomListInnerStyle = css`
  padding: ${space.block2x} ${space.block};
  max-width: ${maxWidth.page1col};
  margin: 0 auto;
  box-sizing: border-box;
  ${moreThanBreakpoint("desktop")} {
    max-width: ${maxWidth.page1colWide};
  }
`;
const priceNoticeBlockStyle = css`
  padding: ${space.atom} ${space.atom2x} ${space.atom2x};
  border-top: 2px solid #ddd;
  border-bottom: 2px solid #ddd;
  margin: ${space.atom3x} ${space.atom};
  text-align: center;
  font-size: ${fontSize.caption};
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom2x};
    margin: ${space.atom3x} ${space.atom3x};
  }
  ${moreThanBreakpoint("desktop")} {
    font-size: ${fontSize.body};
  }
`;
const priceNoticeIconStyle = css`
  font-size: ${fontSize.large};
  vertical-align: middle;
  display: block;
  padding-bottom: ${space.atom};
  opacity: 0.3;
  margin-top: -1px;
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.headline};
    padding-right: ${space.atom};
    padding-bottom: 0;
    display: inline-block;
  }
  ${moreThanBreakpoint("desktop")} {
    margin-top: 0;
  }
`;
const priceNoticeTextStyle = css`
  vertical-align: middle;
  font-weight: ${fontWeight.bold};
`;
const stockNoticeTextStyle = css`
  font-size: ${fontSize.body};
  font-weight: ${fontWeight.bold};
  color: ${accentColor.secondaryColor};
  letter-spacing: 0.1em;
  text-align: center;
  padding: ${space.atom3x} ${space.atom2x};
  margin: 2rem ${space.atom} 0;
  background: rgba(0, 0, 0, 0.025);
  border-radius: ${borderRadius.normal};
  ${moreThanBreakpoint("desktop")} {
    font-size: ${fontSize.mediumHeading};
  }
`;
const linkSwitchStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${accentColor.primaryColor};
  cursor: pointer;
`;
const linkIconStyle = css`
  font-size: ${fontSize.smallest};
  padding-right: ${space.atom0_5x};
`;
